/* eslint-disable @typescript-eslint/ban-types */
import { ReactElement } from 'react'
import ReactDOM from 'react-dom'

import _ from 'lodash'
import moment from 'moment'

import i18n from 'live/services/i18n'

import { fetchIdentifiers, fetchCurves } from '../../../services/api'
import theme from '../../../theme/theme.js'
import { Channel, ChartTemporalBoundary, FilterConfig, KPI, Review, StandardRigChannel } from './types'
import { PERSISTED_CHANNEL_PREFIX } from '../../../kpiConfiguration/utils'

export const lookupTableNames = {
    timeSpanPresets: 'atwTimeSpanPresets',
    reviewOptions: 'atwReviewOptions'
}

export const TransitionTime = 0.3 // seconds
export const MINUTE = 60000 // milliseconds

export const DEFAULT_TRACKS_CHANNELS_IDENTIFIERS: Array<Array<string>> = [
    ['block_position', 'weight_on_hook', 'weight_on_bit'],
    ['fluid_flow', 'standpipe'],
    ['rotary_speed', 'torque'],
    ['bit_depth', 'hole_depth']
]

// export const DEFAULT_IDENTIFIERS: Array<string> =
//     ['Rate of Penetration',
//         'Block Position',
//         'Weight on Bit',
//         'Hole Depth',
//         'SPP Pressure',
//         'Surface Torque',
//         'Mud Flow In',
//         'Hookload',
//         'Bit Depth',
//         'Surface RPM'
//     ]

export const DEFAULT_CHANNELS_KPI_CHANNELS: Map<StandardRigChannel, Channel> = new Map<StandardRigChannel, Channel>()

export const ATW_DEFAULT_KPIS = (themeMode: 'light' | 'dark'): Array<KPI> => [
    { identifier: 'S2S', label: 'S2S', color: theme.kpis[themeMode].s2s },
    { identifier: 'W2W', label: 'W2W', color: theme.kpis[themeMode].w2w },
    { identifier: 'P2P', label: 'P2P', color: theme.kpis[themeMode].p2p },
    { identifier: 'ROP', label: 'ROP', color: theme.kpis[themeMode].rop },
    {
        identifier: 'TRIPPING_IN_SPEED',
        label: 'TRIPPING IN',
        alias: 'TRIP IN',
        color: theme.kpis[themeMode].trip_in
    },
    {
        identifier: 'TRIPPING_OUT_SPEED',
        label: 'TRIPPING OUT',
        alias: 'TRIP OUT',
        color: theme.kpis[themeMode].trip_out
    },
    { identifier: 'TST', label: 'TST', color: theme.kpis[themeMode].tst }
]

// if we need to pass kpi channels to backend
export function transformRigstateChannelsToConfig(rigstateChannels: Map<StandardRigChannel, Channel>): object {
    // transform before submitting
    const entries = {}
    rigstateChannels.forEach(
        (mappedChannel: Channel, keyChannel: StandardRigChannel) =>
            (entries[keyChannel.identifier] = {
                mnemonic: mappedChannel.mnemonic,
                unit: mappedChannel.unit
            } as Channel)
    )

    return entries
}

export async function transformRigstateObjectToMap(
    rigstateChannels: object,
    stackColors: string[]
): Promise<Map<StandardRigChannel, Channel>> {
    const identifiedChannels: any = await fetchIdentifiers()
    const channels: StandardRigChannel[] = Object.keys(rigstateChannels).map(key => {
        const channel = identifiedChannels[key]

        return {
            identifier: key,
            mnemonic: channel.mnemonic,
            name: channel.name,
            // Is this below needed??
            isRigStateChannel: true
        }
    })

    const rigstateChannelsList = []

    const rigStateCurves: any = await fetchCurves()
    Object.entries(rigstateChannels).forEach((entry: any) => {
        const isPersisted = String(entry[1]?.mnemonic)?.startsWith(PERSISTED_CHANNEL_PREFIX)
        // entry[0] is the identifier of the original channel
        // entry[1] the alternative channels object is the,  .mnemonic of the selected alternative channel
        const rigStateChannel = channels.find(ch => ch.identifier == entry[0])
        const selectedChannel = isPersisted
            ? {
                  description: entry[1].mnemonic?.replace(PERSISTED_CHANNEL_PREFIX, ''),
                  unit: '',
                  color: ''
              }
            : rigStateCurves[entry[1].mnemonic as string]
        const channelColor: string = selectedChannel?.color || stackColors?.pop()
        const mappedChannel: Channel = {
            mnemonic: entry[1].mnemonic as string,
            color: channelColor,
            name: selectedChannel?.description,
            unit: selectedChannel?.unit
        }

        rigstateChannelsList.push([rigStateChannel, mappedChannel])
    })

    const rigStateChannelsMap = new Map(rigstateChannelsList) as Map<StandardRigChannel, Channel>

    return rigStateChannelsMap
}
// TODO: REMOVE?
function convertArrayToObject(array: Array<any>, key): object {
    const initialValue = {}
    const reduced = array.reduce((obj, item, index) => {
        return {
            ...obj,
            [index]: item
        }
    }, initialValue)
    // console.log(reduced)

    return reduced
}

export const ANNOTATION_DEFAULT_CONFIG: FilterConfig = {
    startWithAssetType: 'rig',
    timespan: null,
    assetId: null,
    rawTimespan: null,
    review: null,
    kpi_channels: DEFAULT_CHANNELS_KPI_CHANNELS,
    atw_channels: Array.from(DEFAULT_CHANNELS_KPI_CHANNELS.values()),
    channelsByTrack: DEFAULT_TRACKS_CHANNELS_IDENTIFIERS,
    // identifiers: DEFAULT_IDENTIFIERS,
    screenWidth: 1920
}

export function getTimeSpanFromReviewValue(start: number, end: number, review: Review): string {
    return formatTimespan(start, review.value <= 0 ? end : start + review.value * MINUTE)
}
export function annotationValidationFunc(config: FilterConfig): { isValid: boolean; errors: object } {
    const validationResult = {
        isValid: true,
        errors: {
            startWithAssetType: false,
            assetId: false,
            timespan: false,
            review: false
        }
    }

    const selectedAssetIds = config.assetId

    if (!selectedAssetIds) {
        validationResult.errors.assetId = true
        validationResult.isValid = false
    }

    if (!config.rawTimespan) {
        validationResult.errors.timespan = true
        validationResult.isValid = false
    }

    if (!config.review) {
        validationResult.errors.review = true
        validationResult.isValid = false
    }

    return validationResult
}

export function isReadingAndCreatingKPIs(selectedControlButton: KPI): boolean {
    return !_.isNull(selectedControlButton) && selectedControlButton.identifier !== 'stand'
}

export const formatTimespan = (begin: number, end: number): string => `from ts ${begin} to ts ${end}`

export const generateDefaultPendingMessage = (begin?: number, end?: number): string => {
    const beginDay = moment.unix(begin / 1000).format('L')
    const beginFullHour = moment.unix(begin / 1000).format('LT')
    const endDay = moment.unix(end / 1000).format('L')
    const endFullHour = moment.unix(end / 1000).format('LT')

    return `${i18n('Pending review of reprocessed event from')} ${beginDay} ${i18n('at')} ${beginFullHour} ${i18n(
        'to'
    )} ${endDay} ${i18n('at')} ${endFullHour}`
}

export const formatTemporalState = (temporalState: ChartTemporalBoundary): string =>
    formatTimespan(temporalState.begin, temporalState.end)

export function mergeArrays(arr1: Array<any>, arr2: Array<any>, property: string): Array<any> {
    let mergedArray: Array<any> = [...arr1, ...arr2] || []

    for (let i = 0; i < mergedArray.length; i++) {
        for (let j = i + 1; j < mergedArray.length; j++) {
            if (mergedArray[i] && mergedArray[j] && mergedArray[i][property] === mergedArray[j][property]) {
                mergedArray[i] = null
            }
        }
    }
    mergedArray = mergedArray.filter(std => !_.isNull(std))

    return mergedArray
}

export function insertReactComponentInNode(node: HTMLElement, reactComponent: ReactElement): void {
    let element = document.getElementById('modalDiv')
    if (element === null) {
        element = document.createElement('div')
        ReactDOM.render(reactComponent, element)
        node.appendChild(element)
    } else {
        const parentDiv = element.parentElement
        const newElement = document.createElement('div')
        ReactDOM.render(reactComponent, newElement)
        parentDiv.replaceWith(newElement)
    }
}

export const enum KpiServiceTypes {
    rigState = 'rig-state-handler',
    standCount = 'stand-count-handler',
    kpi = 'kpi-handler'
}

export function compareObjectsByField(objectA: object, objectB: object, field: string): boolean {
    return objectA[field] === objectB[field]
}

export function compareObjectsByMultipleFields(objectA: object, objectB: object, fields: Array<string>): boolean {
    return !fields.map((field: string) => compareObjectsByField(objectA, objectB, field)).includes(false)
}

export function HEXtoRGB(hex: string): { r: number; g: number; b: number } {
    const regex = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i
    const result = regex.exec(hex)

    return result
        ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16)
          }
        : null
}

export function parseDateToTimestamp(input: string): number {
    return Date.parse(input)
}

export function checkIfStartIsBiggerThanEnd(value: string): boolean {
    const splitedValue = value?.split(' to ')
    if (splitedValue?.length === 1) return false

    if (splitedValue) {
        const firstValue = parseDateToTimestamp(splitedValue[0])
        const secondValue = parseDateToTimestamp(splitedValue[1])

        if (firstValue > secondValue) return true
    }

    return false
}

// Create an object using the info received from appliedConfig.kpi_channels.
// In the beginner, appliedConfig.kpi_channels uses the config saved on rigstate_channels config to the asset.
export const generateParsedKpiChannels = (kpiChannelsMap: Map<StandardRigChannel, Channel>): Channel[] => {
    const kpiChannels = [...kpiChannelsMap].map(channel => {
        return {
            color: channel[1].color,
            identifier: channel[0].identifier,
            mnemonic: channel[1].mnemonic,
            name: channel[1].name,
            unit: channel[1].unit
        }
    })

    return kpiChannels
}

type durationTypes = {
    days: string
    hours: string
    mins: string
    seconds: string
}

export function handleKpiDuration(duration: number): string {
    let seconds = _.round(duration / 1000)
    let mins = _.floor(seconds / 60)
    let hours = _.floor(mins / 60)
    const days = _.floor(hours / 24)

    // Ensuring that never exceed the limit
    seconds %= 60
    mins %= 60
    hours %= 24

    const result: durationTypes = {
        days: days > 0 ? days + 'd ' : '',
        hours: hours > 0 ? hours + 'h ' : '',
        mins: mins > 0 ? mins + 'm ' : '',
        seconds: seconds > 0 ? seconds + 's' : ''
    }

    return `${result.days}${result.hours}${result.mins}${result.seconds}`
}
